import React from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "lucide-react";

type Tone = "info" | "success" | "warning" | "error";

type InlineAlertProps = {
  tone?: Tone;
  title?: string;
  message: string;
  onAction?: () => void;
  actionLabel?: string;
  className?: string;
  dismissible?: boolean;
  onClose?: () => void;
};

const toneStyles: Record<Tone, { container: string; icon: React.ReactNode }> = {
  info: {
    container: "bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200",
    icon: <Info className="h-4 w-4" />,
  },
  success: {
    container: "bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200",
    icon: <CheckCircle2 className="h-4 w-4" />,
  },
  warning: {
    container: "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200",
    icon: <AlertTriangle className="h-4 w-4" />,
  },
  error: {
    container: "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200",
    icon: <AlertCircle className="h-4 w-4" />,
  },
};

export function InlineAlert({
  tone = "info",
  title,
  message,
  onAction,
  actionLabel,
  className = "",
  dismissible = false,
  onClose,
}: InlineAlertProps) {
  const styles = toneStyles[tone];

  return (
    <div className={`flex items-start gap-2 rounded-xl px-3 py-2 text-sm ${styles.container} ${className}`}>
      <div className="mt-0.5">{styles.icon}</div>
      <div className="flex-1">
        {title && <div className="font-semibold">{title}</div>}
        <div>{message}</div>
      </div>
      {onAction && actionLabel && (
        <button
          onClick={onAction}
          className="ml-2 inline-flex items-center gap-1 text-xs font-semibold underline underline-offset-2 decoration-2 decoration-current hover:opacity-80 transition"
        >
          {actionLabel}
        </button>
      )}
      {dismissible && (
        <button
          onClick={onClose}
          className="ml-2 p-1 rounded-md hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
          aria-label="Schließen"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
